// Bridges this feature's three independently-built conventions so the UI
// layer can wire them together: the validator's dotted-string finding paths
// (lib/validator.ts), the document-edit reducer's index-based element paths
// (lib/document-editor.ts), and a rendered segment's actual position in its
// transaction's flat body list (lib/envelope.ts).

import type {EdiSegment} from './tokenizer';
import type {EdiTransactionExplained} from './explainedEnvelope';
import type {ElementPath} from './document-editor';

/** The path prefix lib/validator.ts uses for everything about one
 * transaction, e.g. "groups[0].transactions[0]". Every finding path and
 * every segment-group base path below is built from this same prefix, so a
 * rendered field's path always matches the finding it should highlight. */
export function transactionPathPrefix(
  groupIndex: number,
  transactionIndex: number
): string {
  return `groups[${groupIndex}].transactions[${transactionIndex}]`;
}

/** Appends the element suffix the validator adds on top of a segment's base
 * path, e.g. "...header.beg" → "...header.beg.elements[2]" for BEG03. */
export function elementFindingPath(
  segmentBasePath: string,
  elementPosition: number
): string {
  return `${segmentBasePath}.elements[${elementPosition - 1}]`;
}

/** Locates a segment's index within its transaction's flat body list — the
 * same list lib/serializer.ts reads from — so a rendered field can dispatch
 * EDIT_ELEMENT against the exact segment instance it's showing, regardless
 * of which loop it's nested under. */
export function segmentIndexIn(
  transaction: EdiTransactionExplained,
  segment: EdiSegment
): number {
  return transaction.segments.indexOf(segment);
}

export function elementPathFor(
  groupIndex: number,
  transactionIndex: number,
  transaction: EdiTransactionExplained,
  segment: EdiSegment,
  elementPosition: number
): ElementPath {
  return {
    groupIndex,
    transactionIndex,
    segmentIndex: segmentIndexIn(transaction, segment),
    elementPosition,
  };
}
